/**
 * Barre de navigation du détail d'une campagne, sur deux niveaux :
 *
 * - les onglets : Parcours et Synthèse (transverses), puis un onglet par test ;
 * - sous un onglet de test, ses sections, en contrôle segmenté — visiblement
 *   secondaire (pilule, sans soulignement) pour ne pas se lire comme un
 *   second rang d'onglets.
 *
 * Présentationnel : ce qui est visible se décide dans `CampagneDetailView`.
 */
import type { TestCampagne } from "@repo/results";
import { Separator } from "@repo/ui";
import { cn } from "@repo/utils";
import { Layers, List } from "lucide-react";
import {
  LIBELLE_SECTION,
  LIBELLE_TEST,
  type OngletCampagne,
  type SectionTest,
} from "./ongletsCampagne";

type Props = {
  tests: readonly TestCampagne[];
  avecSynthese: boolean;
  onglet: OngletCampagne;
  /** Sections du test actif. Moins de deux : rien à choisir, pas de contrôle. */
  sections: readonly SectionTest[];
  section: SectionTest;
  onChoisirOnglet: (onglet: OngletCampagne) => void;
  onChoisirSection: (section: SectionTest) => void;
};

export function NavigationCampagne({
  tests,
  avecSynthese,
  onglet,
  sections,
  section,
  onChoisirOnglet,
  onChoisirSection,
}: Props) {
  const bouton = (valeur: OngletCampagne, libelle: string, icone?: React.ReactNode) => {
    const actif = onglet === valeur;
    return (
      <button
        key={valeur}
        type="button"
        aria-current={actif ? "page" : undefined}
        onClick={() => onChoisirOnglet(valeur)}
        className={cn(
          "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
          actif
            ? "border-primary text-primary"
            : "border-transparent text-muted-foreground hover:text-foreground",
        )}
      >
        {icone}
        {libelle}
      </button>
    );
  };

  return (
    <div className="mb-6 flex flex-col gap-4">
      <nav
        aria-label="Vues de la campagne"
        className="flex flex-wrap items-center gap-1 border-b"
      >
        {bouton("parcours", "Parcours", <List className="h-4 w-4" />)}
        {avecSynthese &&
          bouton("synthese", "Synthèse", <Layers className="h-4 w-4" />)}
        {/* Le filet sépare les vues transverses des vues par test. */}
        <Separator orientation="vertical" className="mx-2 hidden h-5 sm:block" />
        {tests.map((test) => bouton(test, LIBELLE_TEST[test]))}
      </nav>

      {sections.length > 1 && (
        <div
          role="group"
          aria-label="Sections du test"
          className="inline-flex self-start gap-0.5 rounded-full bg-muted p-1"
        >
          {sections.map((s) => {
            const actif = s === section;
            return (
              <button
                key={s}
                type="button"
                aria-pressed={actif}
                onClick={() => onChoisirSection(s)}
                className={cn(
                  "h-8 rounded-full px-4 text-sm font-medium transition-colors",
                  actif
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-charte-hover",
                )}
              >
                {LIBELLE_SECTION[s]}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
